import { Injectable } from '@angular/core';
import { Certificate, createAttachedSignature, getUserCertificates } from 'crypto-pro';

export type SignedCommandT = {
    signature: string;
    signed_payload: string;
    cert_thumbprint: string;
};

export type SignErrorKeyT = 'no_plugin' | 'no_certificates' | 'cancelled' | 'no_token' | 'unknown';

export class CryptoSignError extends Error {
    constructor(public readonly key: SignErrorKeyT, message?: string) {
        super(message ?? key);
    }
}

const CADESPLUGIN_API_URL = 'assets/cadesplugin_api.js';
const PLUGIN_LOAD_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class CryptoSignService {
    private pluginAvailable$: Promise<boolean> | null = null;

    /**
     * Проверяет, доступен ли КриптоПро ЭЦП Browser plug-in. Скрипт cadesplugin_api.js загружается лениво, только здесь:
     * у пользователей без ЭЦП он не грузится вовсе, а его окна (предложение скачать расширение, alert про доверенные узлы)
     * отключены, поэтому без плагина пользователь молча работает с обычным подтверждением.
     */
    isAvailable(): Promise<boolean> {
        if (!this.pluginAvailable$) {
            this.pluginAvailable$ = this.detectPlugin();
        }
        return this.pluginAvailable$;
    }

    async getCertificates(refresh = false): Promise<Certificate[]> {
        try {
            await this.ensureAvailable();
            const certificates = await getUserCertificates(refresh);
            const now = Date.now();
            return certificates.filter((c) => new Date(c.validFrom).getTime() <= now && new Date(c.validTo).getTime() >= now);
        } catch (error) {
            throw this.mapError(error);
        }
    }

    /**
     * Подписывает команду присоединённой подписью (CAdES-BES). Наличие токена проверяется самим подписанием:
     * CSP обращается к закрытому ключу на носителе и запрашивает PIN.
     */
    async signCommand(thumbprint: string, payload: object): Promise<SignedCommandT> {
        const signedPayload = JSON.stringify(this.sortKeys({ ...payload, timestamp: Date.now(), nonce: this.createNonce() }));
        try {
            await this.ensureAvailable();
            const signature = await createAttachedSignature(thumbprint, signedPayload);
            if (!signature) {
                throw new CryptoSignError('unknown');
            }
            return { signature, signed_payload: signedPayload, cert_thumbprint: thumbprint };
        } catch (error) {
            throw this.mapError(error);
        }
    }

    private async ensureAvailable(): Promise<void> {
        // Без этого crypto-pro сам загрузит вшитую старую версию cadesplugin_api.js
        if (!(await this.isAvailable())) {
            throw new CryptoSignError('no_plugin');
        }
    }

    private async detectPlugin(): Promise<boolean> {
        const w = window as any;
        w.cadesplugin_skip_extension_install = true;
        w.cadesplugin_untrusted_sites_disabled_callback = () => console.warn('[CryptoSign] Сайт не в списке доверенных узлов плагина');
        w.cadesplugin_load_timeout = PLUGIN_LOAD_TIMEOUT_MS;

        if (!w.cadesplugin?.LOG_LEVEL_DEBUG && !(await this.loadScript())) {
            return false;
        }
        try {
            await w.cadesplugin;
            return true;
        } catch {
            return false;
        }
    }

    private loadScript(): Promise<boolean> {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = CADESPLUGIN_API_URL;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.head.appendChild(script);
        });
    }

    private mapError(error: any): CryptoSignError {
        if (error instanceof CryptoSignError) {
            return error;
        }
        const message: string = error?.message ?? String(error ?? '');
        console.error('[CryptoSign]', error);
        const lower = message.toLowerCase();
        if (lower.includes('не подключен модуль') || lower.includes('cadesplugin is not defined') || lower.includes('плагин не загружен')) {
            return new CryptoSignError('no_plugin', message);
        }
        if (lower.includes('0x8010006e') || lower.includes('отмен') || lower.includes('cancel')) {
            return new CryptoSignError('cancelled', message);
        }
        if (lower.includes('0x8010001d') || lower.includes('0x80090016') || lower.includes('0x8009000d') || lower.includes('ключ') || lower.includes('носител')) {
            return new CryptoSignError('no_token', message);
        }
        if (lower.includes('сертификат') && lower.includes('не найден')) {
            return new CryptoSignError('no_certificates', message);
        }
        return new CryptoSignError('unknown', message);
    }

    private sortKeys(value: any): any {
        if (Array.isArray(value)) {
            return value.map((v) => this.sortKeys(v));
        }
        if (value && typeof value === 'object') {
            return Object.keys(value)
                .sort()
                .reduce((acc, key) => ({ ...acc, [key]: this.sortKeys(value[key]) }), {});
        }
        return value;
    }

    private createNonce(): string {
        const c: Crypto | undefined = (window as any).crypto;
        if (c?.getRandomValues) {
            return Array.from(c.getRandomValues(new Uint8Array(16)))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        }
        return Math.random().toString(16).slice(2) + Date.now().toString(16);
    }
}
